import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Search, X, Info, ChevronDown } from 'lucide-react';
import { STOCK_LIST, loadStockList, searchStocks } from '../../data/stockList';
import { fetchStockMetadata } from '../../utils/stockMetadataBlob';
import { api } from '../../services/api';
import { loadGenericParquet } from '../../services/duckdbService';
import { fetchEmitenProfile, EmitenProfileData } from '../../services/emitenProfileService';
import { fetchHCLData } from '../../utils/hclBlob';
import { fetchFFData } from '../../utils/ffBlob';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, AreaChart, Area
} from 'recharts';
import Papa from 'papaparse';

interface StoryEmitenProfileProps {
  isDashboard?: boolean;
  selectedStock?: string;
}

export function StoryEmitenProfile({ isDashboard, selectedStock: selectedStockProp }: StoryEmitenProfileProps = {}) {
  const [searchParams] = useSearchParams();
  const urlStock = searchParams.get('stock');

  const [selectedStock, setSelectedStock] = useState(selectedStockProp || urlStock || 'BBCA');
  const [stockInput, setStockInput] = useState(selectedStockProp || urlStock || 'BBCA');
  const [showStockSuggestions, setShowStockSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  
  const [stockMetadata, setStockMetadata] = useState<any>(null);
  
  const [loading, setLoading] = useState(false);
  
  const [shareholdersData, setShareholdersData] = useState<any[]>([]);
  const [holdingData, setHoldingData] = useState<any[]>([]);
  const [moneyflowData, setMoneyflowData] = useState<any[]>([]);
  
  const [onePercentData, setOnePercentData] = useState<any[]>([]);
  const [onePercentLoading, setOnePercentLoading] = useState(false);
  const [onePercentDate, setOnePercentDate] = useState<string>('');
  
  const [hclData, setHclData] = useState<any | null>(null);
  const [ffData, setFfData] = useState<any | null>(null);
  
  const [supabaseData, setSupabaseData] = useState<EmitenProfileData | null>(null);

  const [holdingFilter, setHoldingFilter] = useState<'All' | 'Local' | 'Foreign'>('All');
  const [selectedHoldingYear, setSelectedHoldingYear] = useState<string>('');
  const [selectedHoldingMonth, setSelectedHoldingMonth] = useState<string>('');
  const [holdingChartPeriod, setHoldingChartPeriod] = useState<string>('1Y');

  const holdingSubCategories = [
    { key: 'Individual', name: 'Individual', color: '#1d4ed8' },
    { key: 'Mutual_Fund', name: 'Reksadana', color: '#38bdf8' },
    { key: 'Pension', name: 'Dana Pensiun', color: '#a855f7' },
    { key: 'Insurance', name: 'Asuransi', color: '#ef4444' },
    { key: 'Corporate', name: 'Perusahaan', color: '#16a34a' },
    { key: 'Securities', name: 'Sekuritas', color: '#a16207' },
    { key: 'Bank', name: 'Bank', color: '#be123c' },
    { key: 'Others', name: 'Lainnya', color: '#eab308' }
  ];

  // Constants
  const MAX_DISPLAYED = 10;
  
  useEffect(() => {
    loadStockList().catch(console.error);
    
    // Load all stock metadata for company profile info
    fetchStockMetadata().then(metadataList => {
      const metadataMap = new Map();
      metadataList.forEach((item: any) => {
        metadataMap.set(item.symbol, item);
      });
      setStockMetadata(metadataMap);
    }).catch(console.error);
  }, []);

  const fetchOnePercentShareholders = async (ticker: string, containerUrl: string, sasToken: string) => {
    setOnePercentLoading(true);
    try {
      const isDev = (import.meta as any).env?.DEV || false;
      const baseUrl = isDev ? `/azure-blob-proxy/trading-data-v2` : containerUrl;
      
      const prefix = encodeURIComponent('perubahan_kepemilikan_idx/1%/');
      const listUrl = `${baseUrl}?restype=container&comp=list&prefix=${prefix}&${sasToken}`;
      // SECURITY: log prefix only — listUrl embeds the raw SAS token.
      console.log('Fetching 1% list with prefix:', prefix);
      const res = await fetch(listUrl);
      if (!res.ok) throw new Error(`List failed: ${res.status}`);
      const text = await res.text();
      
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, "text/xml");
      const names = Array.from(xmlDoc.getElementsByTagName("Name")).map(el => el.textContent);
      console.log('1% blob names found:', names);
      
      const csvs = names.filter(n => n?.endsWith('.csv')).sort();
      if (csvs.length === 0) {
        console.warn('No 1% CSV files found!');
        setOnePercentData([]);
        return;
      }
      const latestCsv = csvs[csvs.length - 1];
      console.log('Latest 1% CSV selected:', latestCsv);
      
      const filename = latestCsv!.split('/').pop();
      if (filename) {
        setOnePercentDate(filename.replace('.csv', ''));
      }
      
      const encodedCsvPath = latestCsv!.split('/').map(encodeURIComponent).join('/');
      const csvUrl = `${baseUrl}/${encodedCsvPath}?${sasToken}`;
      // SECURITY: log blob path only — csvUrl embeds the raw SAS token.
      console.log('Fetching 1% CSV:', encodedCsvPath);
      const csvRes = await fetch(csvUrl);
      if (!csvRes.ok) throw new Error(`CSV fetch failed: ${csvRes.status}`);
      const csvText = await csvRes.text();
      
      const parsed = Papa.parse(csvText, { header: true, skipEmptyLines: true, transformHeader: h => h.trim() });
      const allData = parsed.data as any[];
      
      console.log('DEBUG 1% - Sample parsed row from CSV:', allData[0]);
      
      // Helper function to get value resiliently
      const getVal = (row: any, possibleKeys: string[]) => {
        for (const key of possibleKeys) {
          if (row[key] !== undefined && row[key] !== null) return row[key];
        }
        return undefined;
      };

      // Filter by "Kode Efek" (ticker)
      const emitenData = allData.filter(row => {
        const code = getVal(row, ['Kode Efek', 'SHARE_CODE', 'Share Code']);
        return code && typeof code === 'string' && code.trim().toUpperCase() === ticker.trim().toUpperCase();
      });
      
      console.log(`DEBUG 1% - Found ${emitenData.length} rows for ${ticker}. Ticker checked: "${ticker}"`);
      
      // Hitung persentase terbaru jika belum ada
      const enrichedData = emitenData.map(row => {
        let shares = getVal(row, ['Jumlah Saham (Hari Ini)', 'Jumlah Saham ', 'Jumlah Saham', 'TOTAL_HOLDING_SHARES', 'Total Holding Shares']);
        shares = typeof shares === 'string' ? parseFloat(shares.replace(/,/g, '')) : (shares || 0);
        
        let percent = getVal(row, ['Persentase (Hari Ini)', 'Persentase', 'PERCENTAGE', 'Percentage']);
        
        return {
          ...row,
          _parsedShares: shares,
          _investorName: getVal(row, ['Nama Pemegang Saham', 'INVESTOR_NAME', 'Investor Name']) || 'Unknown',
          _percentage: percent
        };
      }).sort((a, b) => b._parsedShares - a._parsedShares);
      
      setOnePercentData(enrichedData);
    } catch (err) {
      console.error('Failed to fetch 1% shareholders:', err);
      setOnePercentData([]);
    } finally {
      setOnePercentLoading(false);
    }
  };

  const fetchProfileData = async (ticker: string) => {
    setLoading(true);
    try {
      const tokenRes = await api.getStockMetadataSasToken();
      if (!tokenRes.success || !tokenRes.data) {
        throw new Error('Failed to get SAS token');
      }
      const { containerUrl, sasToken } = tokenRes.data;
      
      fetchOnePercentShareholders(ticker, containerUrl, sasToken);

      // Fetch the three parquets concurrently + supabase data + HCL/FF
      const [shareholders, holding, moneyflow, supabaseRes, hclRes, ffRes] = await Promise.all([
        loadGenericParquet(`shareholders/${ticker}.parquet`, containerUrl, sasToken).catch(e => {
          console.warn('Failed to load shareholders', e);
          return [];
        }),
        loadGenericParquet(`holding/${ticker}.parquet`, containerUrl, sasToken).catch(e => {
          console.warn('Failed to load holding', e);
          return [];
        }),
        loadGenericParquet(`moneyflow/${ticker}.parquet`, containerUrl, sasToken).catch(e => {
          console.warn('Failed to load moneyflow', e);
          return [];
        }),
        fetchEmitenProfile(ticker).catch(e => {
          console.warn('Failed to load supabase emiten profile', e);
          return null;
        }),
        fetchHCLData().catch(e => {
          console.warn('Failed to load HCL', e);
          return { data: [] };
        }),
        fetchFFData().catch(e => {
          console.warn('Failed to load FF', e);
          return { data: [] };
        })
      ]);

      setShareholdersData(shareholders);
      setHoldingData(holding);
      setMoneyflowData(moneyflow);
      setSupabaseData(supabaseRes);
      
      // HCL and FF filtering for the selected stock
      if (hclRes && hclRes.data) {
        const stockHcl = hclRes.data.filter((d: any) => d['Kode Emiten'] === ticker);
        if (stockHcl.length > 0) {
           stockHcl.sort((a: any, b: any) => new Date(b.Tanggal).getTime() - new Date(a.Tanggal).getTime());
           setHclData(stockHcl[0]); // Get latest HCL record
        } else {
           setHclData(null);
        }
      }
      
      if (ffRes && ffRes.data) {
        const stockFf = ffRes.data.filter((d: any) => d.Kode === ticker);
        if (stockFf.length > 0) {
           setFfData(stockFf[0]);
        } else {
           setFfData(null);
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load Emiten Profile data');
      setShareholdersData([]);
      setHoldingData([]);
      setMoneyflowData([]);
      setSupabaseData(null);
      setHclData(null);
      setFfData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStockProp) {
      setSelectedStock(selectedStockProp);
      setStockInput(selectedStockProp);
    }
  }, [selectedStockProp]);

  useEffect(() => {
    if (selectedStock) {
      fetchProfileData(selectedStock);
    }
  }, [selectedStock]);

  const filteredStocks = searchStocks(stockInput);
  const displayedStocks = filteredStocks.slice(0, MAX_DISPLAYED);
  const hasMore = filteredStocks.length > MAX_DISPLAYED;
  const moreCount = filteredStocks.length - MAX_DISPLAYED;

  const handleStockSelect = (stock: string) => {
    setStockInput(stock);
    setShowStockSuggestions(false);
  };

  const handleShow = () => {
    if (STOCK_LIST.includes(stockInput.toUpperCase())) {
      setSelectedStock(stockInput.toUpperCase());
    } else {
      toast.error('Invalid Ticker');
    }
  };

  const handleStockInputChange = (value: string) => {
    setStockInput(value.toUpperCase());
    setShowStockSuggestions(true);
  };

  // Processing shareholders for the latest date
  const topShareholders = useMemo(() => {
    if (!shareholdersData.length) return [];
    const sortedByDate = [...shareholdersData].sort((a, b) => new Date(b.DataDate).getTime() - new Date(a.DataDate).getTime());
    if(!sortedByDate.length) return [];
    
    const latestDate = sortedByDate[0].DataDate;
    
    return shareholdersData
      .filter(s => s.DataDate === latestDate)
      .sort((a, b) => b.PemegangSaham_Persentase - a.PemegangSaham_Persentase);
  }, [shareholdersData]);

  const controllingShareholders = useMemo(() => {
    return topShareholders.filter(s => s.PemegangSaham_Pengendali).map(s => s.PemegangSaham_Nama);
  }, [topShareholders]);
  
  const holdingYears = useMemo(() => {
    if (!holdingData.length) return [];
    const years = Array.from(new Set(holdingData.map(h => new Date(h.date).getFullYear().toString())));
    return years.sort((a, b) => b.localeCompare(a));
  }, [holdingData]);

  useEffect(() => {
    if (holdingYears.length > 0 && (!selectedHoldingYear || !holdingYears.includes(selectedHoldingYear))) {
      setSelectedHoldingYear(holdingYears[0]);
    }
  }, [holdingYears, selectedHoldingYear]);

  const holdingMonthsForYear = useMemo(() => {
    if (!holdingData.length || !selectedHoldingYear) return [];
    const dates = holdingData.filter(h => new Date(h.date).getFullYear().toString() === selectedHoldingYear).map(h => h.date);
    const uniqueDates = Array.from(new Set(dates));
    return uniqueDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  }, [holdingData, selectedHoldingYear]);

  useEffect(() => {
    if (holdingMonthsForYear.length > 0 && (!selectedHoldingMonth || !holdingMonthsForYear.includes(selectedHoldingMonth))) {
      setSelectedHoldingMonth(holdingMonthsForYear[holdingMonthsForYear.length - 1]);
    }
  }, [holdingMonthsForYear, selectedHoldingMonth]);

  const companyInfo = stockMetadata ? stockMetadata.get(selectedStock) : null;
  const currentOutstandingShares = companyInfo?.shares || 0;

  const outstandingSharesByMonth = useMemo(() => {
    const map = new Map<string, number>();
    const largestShareholderPerDate = new Map<string, { shares: number, pct: number }>();
    
    if (shareholdersData && shareholdersData.length > 0) {
      shareholdersData.forEach(s => {
        if (s.PemegangSaham_Persentase && s.PemegangSaham_Persentase > 0) {
          const existing = largestShareholderPerDate.get(s.DataDate);
          if (!existing || s.PemegangSaham_Persentase > existing.pct) {
            largestShareholderPerDate.set(s.DataDate, {
              shares: s.PemegangSaham_JmlSaham,
              pct: s.PemegangSaham_Persentase
            });
          }
        }
      });
      
      largestShareholderPerDate.forEach((val, date) => {
        map.set(date, val.shares / (val.pct / 100));
      });
    }
    return map;
  }, [shareholdersData]);

  const getOutstandingShares = (date: string) => {
    if (outstandingSharesByMonth.has(date)) return outstandingSharesByMonth.get(date)!;
    const sortedDates = Array.from(outstandingSharesByMonth.keys()).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    let bestDate = null;
    for (const d of sortedDates) {
      if (new Date(d).getTime() <= new Date(date).getTime()) {
        bestDate = d;
      }
    }
    if (bestDate) return outstandingSharesByMonth.get(bestDate)!;
    return currentOutstandingShares;
  };

  const selectedMonthHolding = useMemo(() => {
    if (!holdingData.length || !selectedHoldingMonth) return null;
    const monthData = holdingData.find(h => h.date === selectedHoldingMonth);
    if (!monthData) return null;

    const types = ['Corporate', 'Individual', 'Mutual Fund', 'Insurance', 'Pension Fund', 'Financial Institution', 'Securities Company', 'Foundation', 'Others'];
    
    let monthOutstanding = getOutstandingShares(monthData.date);
    if (!monthOutstanding || monthOutstanding <= 0) monthOutstanding = currentOutstandingShares > 0 ? currentOutstandingShares : (monthData.total_value || 1);
    
    const totalDenominator = monthOutstanding;

    const comps = types.map(type => {
      const locVal = monthData[`local_${type}_value`] || 0;
      const forVal = monthData[`foreign_${type}_value`] || 0;
      const totalVal = locVal + forVal;
      const percent = totalDenominator ? (totalVal / totalDenominator) * 100 : 0;
      return {
        name: type,
        percent: percent,
        value: totalVal
      };
    }).sort((a, b) => b.percent - a.percent).filter(t => t.percent > 0);
    
    return {
       date: monthData.date,
       totalShares: totalDenominator,
       compositions: comps
    };
  }, [holdingData, selectedHoldingMonth, currentOutstandingShares, outstandingSharesByMonth]);

  // Holding History Chart Data (Local vs Foreign)
  const holdingHistoryData = useMemo(() => {
    if (!holdingData.length) return [];
    const sorted = [...holdingData].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const mappedData = sorted.map(item => {
      const types = ['Pension Fund', 'Insurance', 'Individual', 'Mutual Fund', 'Securities Company', 'Financial Institution', 'Corporate', 'Others', 'Foundation'];
      
      const localSum = types.reduce((sum, t) => sum + (item[`local_${t}_value`] || 0), 0);
      const foreignSum = types.reduce((sum, t) => sum + (item[`foreign_${t}_value`] || 0), 0);
      let monthOutstanding = getOutstandingShares(item.date);
      if (!monthOutstanding || monthOutstanding <= 0) monthOutstanding = currentOutstandingShares > 0 ? currentOutstandingShares : (item.total_value || 1);
      const totalOverall = monthOutstanding;

      const result: any = {
        date: item.date,
        Local: (localSum / totalOverall) * 100,
        Foreign: (foreignSum / totalOverall) * 100
      };
      
      const getVal = (prefix: string, ts: string[]) => {
        return ts.reduce((sum, t) => sum + (((item[`${prefix}_${t}_value`] || 0) / totalOverall) * 100), 0);
      };

      result.Local_Pension = getVal('local', ['Pension Fund']);
      result.Local_Insurance = getVal('local', ['Insurance']);
      result.Local_Individual = getVal('local', ['Individual']);
      result.Local_Mutual_Fund = getVal('local', ['Mutual Fund']);
      result.Local_Securities = getVal('local', ['Securities Company']);
      result.Local_Bank = getVal('local', ['Financial Institution']);
      result.Local_Corporate = getVal('local', ['Corporate']);
      result.Local_Others = getVal('local', ['Others', 'Foundation']);

      result.Foreign_Pension = getVal('foreign', ['Pension Fund']);
      result.Foreign_Insurance = getVal('foreign', ['Insurance']);
      result.Foreign_Individual = getVal('foreign', ['Individual']);
      result.Foreign_Mutual_Fund = getVal('foreign', ['Mutual Fund']);
      result.Foreign_Securities = getVal('foreign', ['Securities Company']);
      result.Foreign_Bank = getVal('foreign', ['Financial Institution']);
      result.Foreign_Corporate = getVal('foreign', ['Corporate']);
      result.Foreign_Others = getVal('foreign', ['Others', 'Foundation']);

      return result;
    });
    
    if (holdingChartPeriod !== 'ALL') {
      const cutoff = new Date();
      if (holdingChartPeriod === '1Y') cutoff.setFullYear(cutoff.getFullYear() - 1);
      else if (holdingChartPeriod === '3Y') cutoff.setFullYear(cutoff.getFullYear() - 3);
      else if (holdingChartPeriod === '5Y') cutoff.setFullYear(cutoff.getFullYear() - 5);
      else if (holdingChartPeriod === 'YTD') {
        cutoff.setMonth(0, 1);
        cutoff.setHours(0,0,0,0);
      }
      return mappedData.filter((item: any) => new Date(item.date).getTime() >= cutoff.getTime());
    }
    
    return mappedData;
  }, [holdingData, holdingChartPeriod, currentOutstandingShares, outstandingSharesByMonth]);

  // Number of Shareholders history
  const numOfShareholdersHistory = useMemo(() => {
    if (!shareholdersData.length) return [];
    
    // Group by DataDate
    const map = new Map<string, number>();
    shareholdersData.forEach(s => {
      if (!map.has(s.DataDate)) {
         map.set(s.DataDate, s.JumlahPemegangSaham);
      }
    });
    
    const sortedDates = Array.from(map.keys()).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    
    return sortedDates.map((date, idx) => {
       const current = map.get(date) || 0;
       const previous = idx < sortedDates.length - 1 ? (map.get(sortedDates[idx + 1]) || current) : current;
       const diff = current - previous;
       return {
         date,
         count: current,
         diff
       };
    });
  }, [shareholdersData]);

  const formatNumber = (num: number) => {
    if (!num) return '0';
    return num.toLocaleString('en-US');
  };

  const formatCompact = (num: number) => {
    if (!num) return '0';
    if (num >= 1e9) return (num / 1e9).toFixed(2) + ' B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + ' M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + ' K';
    return num.toString();
  };

  const formatPercent = (num: number) => {
    if (num === undefined || num === null) return '0.00%';
    return num.toFixed(2) + '%';
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border p-3 rounded-md shadow-lg text-xs">
          <p className="font-semibold text-foreground mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex justify-between items-center gap-4 mb-1">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                <span style={{ color: entry.color }}>{entry.name}</span>
              </span>
              <span className="font-mono text-foreground font-medium">
                {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}%
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };
  
  const latestFreeFloat = useMemo(() => {
    if (!ffData) return null;
    let latestDate = 0;
    let latestVal = null;
    for (const key of Object.keys(ffData)) {
      if (key.includes('% Saham Free Float (FF)')) {
        const dateMatch = key.match(/^(.*?)\s*\(/);
        if (dateMatch) {
          const dateStr = dateMatch[1].trim();
          const d = new Date(dateStr).getTime();
          if (d > latestDate) {
            latestDate = d;
            latestVal = ffData[key];
          }
        }
      }
    }
    if (latestVal !== null) {
      const num = parseFloat(String(latestVal).replace(',', '.'));
      if (!isNaN(num)) return (num * 100).toFixed(2) + '%';
    }
    return 'N/A';
  }, [ffData]);

  const latestHcl = useMemo(() => {
    if (!hclData) return null;
    const val = hclData['HCL (%)'];
    if (val !== undefined && val !== null) {
      const num = parseFloat(String(val).replace(',', '.'));
      if (!isNaN(num)) return num.toFixed(2) + '%';
    }
    return null;
  }, [hclData]);
  
  const formatDateStr = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-0 h-full flex flex-col min-h-0">
      {/* Header Search - Hide if in dashboard */}
      {!isDashboard && (
      <div className="bg-[#0a0f20]/95 border-b border-[#3a4252] px-4 py-1.5 backdrop-blur-md shadow-lg lg:sticky lg:top-0 lg:z-40">
        <div className="flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center gap-3 md:gap-6 w-full">
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center w-full justify-between">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">
                Ticker:
              </label>
              <div className="relative stock-dropdown-container w-32">
                <div className="w-full h-9 px-3 flex items-center justify-between bg-background border border-input rounded-md text-sm text-foreground hover:border-primary/50 focus-within:border-primary/50 transition-colors">
                  <Search className="w-3.5 h-3.5 text-muted-foreground mr-2 flex-shrink-0" />
                  <input
                    type="text"
                    value={stockInput}
                    onChange={(e) => handleStockInputChange(e.target.value)}
                    onFocus={() => setShowStockSuggestions(true)}
                    onKeyDown={(e) => {
                      if (!showStockSuggestions) setShowStockSuggestions(true);
                      if (e.key === 'ArrowDown' && displayedStocks.length) {
                        e.preventDefault();
                        setHighlightedIndex((prev) => (prev + 1) % displayedStocks.length);
                      } else if (e.key === 'ArrowUp' && displayedStocks.length) {
                        e.preventDefault();
                        setHighlightedIndex((prev) => (prev <= 0 ? displayedStocks.length - 1 : prev - 1));
                      } else if (e.key === 'Enter') {
                        if (highlightedIndex >= 0 && displayedStocks[highlightedIndex]) {
                          handleStockSelect(displayedStocks[highlightedIndex]);
                          setHighlightedIndex(-1);
                        }
                      } else if (e.key === 'Escape') {
                        setShowStockSuggestions(false);
                        setHighlightedIndex(-1);
                      }
                    }}
                    className="w-full bg-transparent text-sm text-foreground focus:outline-none placeholder:text-muted-foreground/50 truncate uppercase font-semibold"
                    placeholder="Select ticker..."
                  />
                  {stockInput ? (
                    <X 
                      className="w-3.5 h-3.5 text-muted-foreground opacity-50 ml-1 flex-shrink-0 cursor-pointer hover:opacity-100" 
                      onClick={() => {
                        setStockInput('');
                        setShowStockSuggestions(true);
                      }} 
                    />
                  ) : (
                    <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0 cursor-pointer" onClick={() => setShowStockSuggestions(!showStockSuggestions)} />
                  )}
                </div>
            {showStockSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg z-50 max-h-64 overflow-y-auto py-1">
                {displayedStocks.length > 0 ? (
                  <>
                    {displayedStocks.map((stock, idx) => (
                      <div
                        key={stock}
                        onClick={() => handleStockSelect(stock)}
                        onMouseEnter={() => setHighlightedIndex(idx)}
                        onMouseLeave={() => setHighlightedIndex(-1)}
                        className={`px-3 py-[2.06px] text-sm cursor-pointer hover:bg-accent flex justify-between items-center ${idx === highlightedIndex ? 'bg-accent/50 text-primary font-medium' : 'text-foreground'}`}
                      >
                        <span>{stock}</span>
                      </div>
                    ))}
                    {hasMore && (
                      <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border">
                        +{moreCount} more stocks
                      </div>
                    )}
                  </>
                ) : (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    No stocks found
                  </div>
                )}
              </div>
            )}
              </div>
              <Button onClick={handleShow} size="sm" variant="default" className="h-9 px-4 font-semibold shrink-0">
                Show
              </Button>
            </div>
            {companyInfo && (
               <div className="flex flex-col justify-center text-left sm:text-right overflow-hidden mt-2 sm:mt-0">
                 <span className="text-sm font-semibold text-foreground tracking-wide truncate">{companyInfo.name}</span>
                 <span className="text-xs text-muted-foreground truncate">{companyInfo.sector}</span>
               </div>
            )}
          </div>
        </div>
      </div>
      )}

      <div className={`px-3 py-2 space-y-2 flex-1 min-h-0 relative ${isDashboard ? 'overflow-y-auto' : ''}`}>
        
        {(loading || onePercentLoading) ? (
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-primary/20 overflow-hidden z-50">
            <div 
              className="h-full bg-blue-500 w-1/3"
              style={{
                animation: 'progress-infinite 1.5s ease-in-out infinite',
              }}
            />
            <style>{`
              @keyframes progress-infinite {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(300%); }
              }
            `}</style>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            
            <div className="flex flex-col-reverse gap-2">
            {/* Top Row (Now renders below Bottom Row due to flex-col-reverse) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              
              {/* Shareholder Composition */}
              <Card className="h-full border border-white/40 bg-card/40 backdrop-blur overflow-hidden gap-0 rounded-sm">
                <div className="px-3 pr-4 py-2 border-b border-border/50 flex flex-row items-center justify-between">
                   <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">Shareholder Composition</span>
                     <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                   </div>
                   {holdingYears.length > 0 && (
                     <div className="flex items-center gap-1 text-[11px] text-muted-foreground font-medium">
                       <select 
                         value={selectedHoldingYear}
                         onChange={e => setSelectedHoldingYear(e.target.value)}
                         className="bg-transparent outline-none border-none cursor-pointer appearance-none text-right"
                       >
                         {holdingYears.map(y => <option key={y} value={y} className="bg-background">{y}</option>)}
                       </select>
                       <ChevronDown className="w-3 h-3" />
                     </div>
                   )}
                </div>
                   <div className="p-0">
                    <div className="px-3 pr-4 pt-2">
                    <div className="flex items-center gap-1 overflow-x-auto pb-1 mb-1 scrollbar-none">
                     {holdingMonthsForYear.map(date => {
                        const d = new Date(date);
                        const monthStr = d.toLocaleString('en-US', { month: 'short' });
                        const isSelected = date === selectedHoldingMonth;
                        return (
                          <button key={date} onClick={() => setSelectedHoldingMonth(date)} className={`px-2 py-0.5 min-w-[40px] text-center text-[10px] font-semibold rounded border transition-colors ${isSelected ? 'border-emerald-500 text-emerald-500 bg-emerald-500/10' : 'border-border/50 text-muted-foreground hover:bg-muted'}`}>{monthStr}</button>
                        );
                     })}
                    </div>
                    {selectedMonthHolding ? (
                      <div>
                        <div className="flex justify-between items-center border border-border/30 rounded px-2 py-1 bg-muted/5 mb-1">
                          <span className="text-[11px] text-muted-foreground">Total Shares</span>
                          <span className="text-sm font-bold text-foreground">
                            {formatCompact(selectedMonthHolding.totalShares)}
                          </span>
                        </div>
                        <div className="overflow-y-auto max-h-[220px] space-y-1 pr-1 pb-2 scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent">
                          {selectedMonthHolding.compositions.slice(0, 10).map((comp, idx) => (
                             <div key={idx} className="space-y-0.5 py-0.5">
                               <div className="flex justify-between items-center">
                                 <div className="flex items-center gap-1.5">
                                   <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                                   <span className="text-[11px] font-medium text-foreground/90">{comp.name}</span>
                                   <span className="text-[10px] text-muted-foreground">{formatCompact(comp.value)}</span>
                                 </div>
                                 <span className="text-[11px] font-semibold">{comp.percent.toFixed(2)}%</span>
                               </div>
                               <div className="h-1 w-full bg-muted rounded-full overflow-hidden">
                                 <div 
                                   className="h-full bg-primary transition-all duration-500 rounded-full" 
                                   style={{ width: `${Math.min(comp.percent, 100)}%` }}
                                 />
                               </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
                        No holding data available.
                      </div>
                    )}
                    </div>
                   </div>
              </Card>

              {/* Holding Composition Chart */}
              <Card className="h-full border border-white/40 bg-card/40 backdrop-blur overflow-hidden gap-0 rounded-sm">
                 <div className="px-3 pr-4 py-2 border-b border-border/50 flex flex-row items-center justify-between">
                    <div className="flex items-center gap-2">
                       <span className="text-sm font-semibold">Holding Composition</span>
                      <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </div>
                    {holdingHistoryData.length > 0 && (
                      <span className="text-[11px] text-muted-foreground">
                        Last Updated: {formatDateStr(holdingHistoryData[holdingHistoryData.length - 1].date)}
                      </span>
                    )}
                 </div>
                 <div className="p-2">
                   {holdingHistoryData.length > 0 ? (
                     <div className="space-y-1.5">
                       <div className="flex items-center justify-between">
                         <div className="flex items-center gap-2">
                           <button onClick={() => setHoldingFilter('All')} className={`px-3 py-0.5 text-[11px] font-medium rounded-full border ${holdingFilter === 'All' ? 'border-emerald-500 text-emerald-500 bg-emerald-500/10' : 'border-border text-muted-foreground hover:bg-muted'}`}>All</button>
                           <button onClick={() => setHoldingFilter('Local')} className={`px-3 py-0.5 text-[11px] font-medium rounded-full border ${holdingFilter === 'Local' ? 'border-emerald-500 text-emerald-500 bg-emerald-500/10' : 'border-border text-muted-foreground hover:bg-muted'}`}>Local</button>
                           <button onClick={() => setHoldingFilter('Foreign')} className={`px-3 py-0.5 text-[11px] font-medium rounded-full border ${holdingFilter === 'Foreign' ? 'border-emerald-500 text-emerald-500 bg-emerald-500/10' : 'border-border text-muted-foreground hover:bg-muted'}`}>Foreign</button>
                         </div>
                         <select
                           value={holdingChartPeriod}
                           onChange={e => setHoldingChartPeriod(e.target.value)}
                           className="bg-transparent text-primary font-medium outline-none border-none cursor-pointer text-right appearance-none text-[11px]"
                         >
                           <option value="1Y" className="bg-background">1 Tahun</option>
                           <option value="3Y" className="bg-background">3 Tahun</option>
                           <option value="5Y" className="bg-background">5 Tahun</option>
                           <option value="YTD" className="bg-background">YTD</option>
                           <option value="ALL" className="bg-background">All Time</option>
                         </select>
                       </div>
                      <div className="h-[220px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={holdingHistoryData}
                            margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" opacity={0.3} />
                            <XAxis 
                              dataKey="date" 
                              tickFormatter={(tick) => {
                                const d = new Date(tick);
                                return `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear().toString().substr(-2)}`;
                              }} 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} 
                              minTickGap={30}
                            />
                            <YAxis 
                              domain={[0, 'auto']} 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} 
                              tickFormatter={(val) => `${Number.isInteger(val) ? val : val.toFixed(1)}%`}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend iconType="circle" wrapperStyle={{ fontSize: '11px', color: 'hsl(var(--muted-foreground))' }} />
                            {holdingFilter === 'All' && (
                              <>
                                <Line type="monotone" dataKey="Local" name="Local" stroke="#8b5cf6" strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: '#8b5cf6' }} />
                                <Line type="monotone" dataKey="Foreign" name="Foreign" stroke="#10b981" strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: '#10b981' }} />
                              </>
                            )}
                            {holdingFilter === 'Local' && holdingSubCategories.map(sub => (
                              <Line key={sub.key} type="monotone" dataKey={`Local_${sub.key}`} name={sub.name} stroke={sub.color} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: sub.color }} />
                            ))}
                            {holdingFilter === 'Foreign' && holdingSubCategories.map(sub => (
                              <Line key={sub.key} type="monotone" dataKey={`Foreign_${sub.key}`} name={sub.name} stroke={sub.color} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: sub.color }} />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : (
                     <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">
                       No history data available.
                     </div>
                  )}
                </div>
              </Card>

            </div>

            {/* Bottom Row */}
            <div className="grid grid-cols-1 lg:grid-cols-[4fr_4fr_3fr] gap-2 items-stretch lg:h-[350px]">
              
              {/* Column 1: Shareholders & Free Float */}
              <div className="flex flex-col gap-2 h-full order-2">
                {/* Shareholders List */}
                <Card className="flex-1 flex flex-col border border-white/40 bg-card/40 backdrop-blur overflow-hidden gap-0 rounded-sm">
                  <div className="px-3 pr-4 py-2 border-b border-border/50 shrink-0">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold">Shareholders</span>
                      <span className="text-[11px] font-medium text-muted-foreground">Shares</span>
                    </div>
                  </div>
                  <div className="p-0 flex-1 min-h-0 relative">
                    {topShareholders.length > 0 ? (
                      <div className="absolute inset-0 overflow-y-auto scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent">
                        <table className="w-full">
                          <tbody>
                              {topShareholders.map((s, idx) => (
                                <tr key={idx} className="border-b border-border/30 hover:bg-muted/20 transition-colors">
                                  <td className="px-3 py-1.5">
                                    <div className="font-semibold text-foreground/90 uppercase text-[11px] line-clamp-1">{s.PemegangSaham_Nama}</div>
                                  </td>
                                   <td className="px-3 py-1.5 text-right whitespace-nowrap">
                                    <div className="flex items-center justify-end">
                                      <span className="text-[11px] font-medium text-foreground">{formatCompact(s.PemegangSaham_JmlSaham)}</span>
                                      <span className="text-[10px] text-muted-foreground w-[52px] text-right inline-block">({formatPercent(s.PemegangSaham_Persentase)})</span>
                                    </div>
                                   </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                    ) : (
                      <div className="p-8 text-center text-muted-foreground text-sm">
                        No shareholder data found.
                      </div>
                    )}
                  </div>
                </Card>
                
                {/* Free Float Box */}
                <Card className="shrink-0 border border-white/40 bg-card/40 backdrop-blur overflow-hidden gap-0 rounded-sm">
                  <div className="px-3 py-2 flex justify-between items-center">
                    <span className="text-sm font-semibold">Free Float</span>
                    <span className="text-sm font-bold text-foreground">{latestFreeFloat || 'N/A'}</span>
                  </div>
                </Card>
              </div>

              {/* Column 2: >1% Shareholders & HCL */}
              <div className="flex flex-col gap-2 h-full order-1">
                {/* >1% Shareholders (Daily) */}
                <Card className="flex-1 flex flex-col border border-white/40 bg-card/40 backdrop-blur overflow-hidden gap-0 rounded-sm">
                  <div className="px-3 pr-4 py-2 border-b border-border/50 shrink-0">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold">&gt;1% Shareholders</span>
                    <span className="text-[11px] font-medium text-muted-foreground">Shares</span>
                  </div>
                </div>
                <div className="p-0 flex-1 min-h-0 relative">
                  {onePercentLoading ? (
                    <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                      Loading...
                    </div>
                  ) : onePercentData.length > 0 ? (
                    <div className="absolute inset-0 overflow-y-auto scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent">
                      <table className="w-full">
                        <tbody>
                            {onePercentData.map((s, idx) => (
                              <tr key={idx} className="border-b border-border/30 hover:bg-muted/20 transition-colors">
                                <td className="px-3 py-1.5">
                                  <div className="font-semibold text-foreground/90 uppercase text-[11px] line-clamp-1">{s._investorName}</div>
                                </td>
                                 <td className="px-3 py-1.5 text-right whitespace-nowrap">
                                  <div className="flex items-center justify-end">
                                    <span className="text-[11px] font-medium text-foreground">{formatCompact(s._parsedShares)}</span>
                                    <span className="text-[10px] text-muted-foreground w-[52px] text-right inline-block">({s._percentage}%)</span>
                                  </div>
                                 </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                      No 1% shareholder data found.
                    </div>
                  )}
                </div>
              </Card>

              {/* HCL Box (if present) */}
              {latestHcl && (
                <Card className="shrink-0 border border-white/40 bg-card/40 backdrop-blur overflow-hidden gap-0 rounded-sm">
                  <div className="px-3 py-2 flex justify-between items-center">
                    <span className="text-sm font-semibold">High Concentration (HCL)</span>
                    <span className="text-sm font-bold text-foreground">{latestHcl}</span>
                  </div>
                </Card>
              )}
            </div>

            {/* Column 3: Number of Shareholders */}
            <div className="flex flex-col gap-2 h-full order-3">
              <Card className="flex-1 flex flex-col border border-white/40 bg-card/40 backdrop-blur overflow-hidden gap-0 rounded-sm">
                <div className="px-3 pr-4 py-2 border-b border-border/50 shrink-0">
                  <span className="text-sm font-semibold">Number of Shareholders</span>
                </div>
                <div className="p-0 flex-1 min-h-0 relative">
                  {numOfShareholdersHistory.length > 0 ? (
                    <div className="absolute inset-0 overflow-y-auto scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent">
                      <table className="w-full">
                        <tbody>
                          {numOfShareholdersHistory.map((item, idx) => (
                            <tr key={idx} className="border-b border-border/30 hover:bg-muted/20 transition-colors">
                              <td className="px-3 py-1.5">
                                <div className="text-foreground/90 text-[11px]">{formatDateStr(item.date)}</div>
                              </td>
                              <td className="px-3 py-1.5 text-right">
                                <div className="flex items-center justify-end">
                                  <span className="text-[11px] font-medium text-foreground">{formatNumber(item.count)}</span>
                                  <span className={`text-[10px] font-medium w-[56px] text-right inline-block ${item.diff > 0 ? 'text-emerald-500' : item.diff < 0 ? 'text-rose-500' : 'text-transparent'}`}>
                                    {item.diff !== 0 ? `(${item.diff > 0 ? '+' : ''}${formatNumber(item.diff)})` : ''}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                      No historical data available.
                    </div>
                  )}
                </div>
              </Card>
            </div>
            </div>
            </div>

            {/* Supabase Profile Data Integration */}
            {supabaseData && supabaseData.profile && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 mt-2">
                  
                  {/* Left Column: Directors & Commissioners */}
                  <div className="col-span-1 space-y-2">
                    <Card className="h-full border border-white/40 bg-card/40 backdrop-blur overflow-hidden gap-0 rounded-sm">
                      <div className="px-3 pr-4 py-2 border-b border-border/50">
                        <span className="text-sm font-semibold">Directors & Commissioners</span>
                      </div>
                      <div className="p-0 max-h-[300px] overflow-y-auto">
                        <table className="w-full">
                          <tbody>
                            {supabaseData.directors.map((d, idx) => (
                              <tr key={`dir-${idx}`} className="border-b border-border/30 hover:bg-muted/20">
                                <td className="px-3 py-1.5">
                                  <div className="flex items-center gap-1.5">
                                    <div className="text-[11px] font-semibold text-foreground/90 uppercase line-clamp-1" title={d.nama}>{d.nama}</div>
                                    <div className="flex gap-1 shrink-0">
                                      <span className="px-1 py-0 rounded border border-border text-[9px] text-muted-foreground bg-muted/20">D</span>
                                      {d.afiliasi && <span className="px-1 py-0 rounded border border-border text-[9px] text-muted-foreground bg-muted/20">A</span>}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  <div className="text-[10px] text-muted-foreground">{d.jabatan}</div>
                                </td>
                              </tr>
                            ))}
                            {supabaseData.commissioners.map((c, idx) => (
                              <tr key={`com-${idx}`} className="border-b border-border/30 hover:bg-muted/20">
                                <td className="px-3 py-1.5">
                                  <div className="flex items-center gap-1.5">
                                    <div className="text-[11px] font-semibold text-foreground/90 uppercase line-clamp-1" title={c.nama}>{c.nama}</div>
                                    <div className="flex gap-1 shrink-0">
                                      <span className="px-1 py-0 rounded border border-border text-[9px] text-muted-foreground bg-muted/20">K</span>
                                      {c.independen && <span className="px-1 py-0 rounded border border-border text-[9px] text-muted-foreground bg-muted/20">I</span>}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  <div className="text-[10px] text-muted-foreground">{c.jabatan}</div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  </div>

                  {/* Center Column: Company History & UBO */}
                  <div className="col-span-1 space-y-2">
                    <Card className="border border-white/40 bg-card/40 backdrop-blur overflow-hidden gap-0 rounded-sm">
                      <div className="px-3 pr-4 py-2 border-b border-border/50">
                        <span className="text-sm font-semibold">Company History</span>
                      </div>
                      <div className="p-0">
                        <table className="w-full text-[11px]">
                          <tbody>
                            <tr className="border-b border-border/30 hover:bg-muted/20">
                              <td className="px-3 py-2 text-muted-foreground">Listing Date</td>
                              <td className="px-3 py-2 text-right font-medium">{formatDateStr(supabaseData.profile.tanggal_pencatatan)}</td>
                            </tr>
                            <tr className="border-b border-border/30 hover:bg-muted/20">
                              <td className="px-3 py-2 text-muted-foreground">Listing Board</td>
                              <td className="px-3 py-2 text-right font-medium">{supabaseData.profile.papan_pencatatan || '-'}</td>
                            </tr>
                            <tr className="border-b border-border/30 hover:bg-muted/20">
                              <td className="px-3 py-2 text-muted-foreground">Sector</td>
                              <td className="px-3 py-2 text-right font-medium">{supabaseData.profile.sektor || '-'}</td>
                            </tr>
                            <tr className="border-b border-border/30 hover:bg-muted/20">
                              <td className="px-3 py-2 text-muted-foreground">Sub Sector</td>
                              <td className="px-3 py-2 text-right font-medium">{supabaseData.profile.sub_sektor || '-'}</td>
                            </tr>
                            <tr className="border-b border-border/30 hover:bg-muted/20">
                              <td className="px-3 py-2 text-muted-foreground">Industry</td>
                              <td className="px-3 py-2 text-right font-medium">{supabaseData.profile.industri || '-'}</td>
                            </tr>
                            <tr className="hover:bg-muted/20">
                              <td className="px-3 py-2 text-muted-foreground">BAE</td>
                              <td className="px-3 py-2 text-right font-medium truncate max-w-[120px]" title={supabaseData.profile.bae || ''}>{supabaseData.profile.bae || '-'}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </Card>

                    <Card className="border border-white/40 bg-card/40 backdrop-blur overflow-hidden gap-0 rounded-sm">
                      <div className="px-3 pr-4 py-2 border-b border-border/50">
                        <span className="text-sm font-semibold">Ultimate Beneficiary Owner</span>
                      </div>
                      <div className="p-0">
                        {supabaseData.shareholders.filter((s: any) => s.pengendali).length > 0 ? (
                          <div className="flex flex-col">
                            {supabaseData.shareholders.filter((s: any) => s.pengendali).map((u: any, idx: number) => (
                              <div key={idx} className="px-3 py-2 border-b border-border/30 text-[11px] font-semibold uppercase text-foreground/90">
                                {u.nama}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-4 text-center text-xs text-muted-foreground">Not found.</div>
                        )}
                      </div>
                    </Card>
                  </div>

                  {/* Right Column: Address */}
                  <div className="col-span-1 space-y-2">
                    <Card className="h-full border border-white/40 bg-card/40 backdrop-blur overflow-hidden gap-0 rounded-sm">
                      <div className="px-3 pr-4 py-2 border-b border-border/50">
                        <span className="text-sm font-semibold">Address</span>
                      </div>
                      <div className="p-4 space-y-3">
                        {supabaseData.profile.logo_url && (
                          <div className="flex justify-center mb-4">
                            <img src={supabaseData.profile.logo_url} alt={`${supabaseData.profile.nama_emiten} Logo`} className="h-16 object-contain" />
                          </div>
                        )}
                        <div className="text-[11px] text-foreground/80 leading-relaxed font-medium">
                          {supabaseData.profile.alamat || '-'}
                        </div>
                        <div className="text-[11px] text-muted-foreground space-y-1">
                          <div className="flex justify-between"><span className="w-16">Phone:</span> <span className="text-foreground/90 font-medium text-right break-all">{supabaseData.profile.telepon || '-'}</span></div>
                          <div className="flex justify-between"><span className="w-16">Fax:</span> <span className="text-foreground/90 font-medium text-right break-all">{supabaseData.profile.fax || '-'}</span></div>
                          <div className="flex justify-between"><span className="w-16">Email:</span> <span className="text-foreground/90 font-medium text-right break-all text-primary hover:underline cursor-pointer">{supabaseData.profile.email || '-'}</span></div>
                          <div className="flex justify-between"><span className="w-16">Website:</span> <span className="text-foreground/90 font-medium text-right break-all text-primary hover:underline cursor-pointer">{supabaseData.profile.website || '-'}</span></div>
                        </div>
                      </div>
                    </Card>
                  </div>

                </div>

                {/* Subsidiaries */}
                <Card className="border border-white/40 bg-card/40 backdrop-blur overflow-hidden gap-0 rounded-sm mt-2">
                  <div className="px-3 pr-4 py-2 flex flex-row items-center justify-between border-b border-border/50">
                    <span className="text-sm font-semibold">Subsidiary Companies</span>
                  </div>
                  <div className="p-0 overflow-x-auto">
                    {supabaseData.subsidiaries.length > 0 ? (
                      <div className="max-h-[260px] overflow-y-auto scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent">
                        <table className="w-full text-[11px] min-w-[600px]">
                        <thead className="bg-muted/20">
                          <tr className="border-b border-border/50">
                            <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Company</th>
                            <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Business Type</th>
                            <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Location</th>
                            <th className="px-3 py-2 text-right font-semibold text-muted-foreground">Commercial Year</th>
                            <th className="px-3 py-2 text-right font-semibold text-muted-foreground">Total Asset</th>
                            <th className="px-3 py-2 text-right font-semibold text-muted-foreground">Percentage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {supabaseData.subsidiaries.map((sub, idx) => (
                            <tr key={idx} className="border-b border-border/30 hover:bg-muted/10">
                              <td className="px-3 py-2 font-medium text-foreground/90 uppercase">{sub.nama}</td>
                              <td className="px-3 py-2 text-muted-foreground">{sub.bidang_usaha || '-'}</td>
                              <td className="px-3 py-2 text-muted-foreground">{sub.lokasi || '-'}</td>
                              <td className="px-3 py-2 text-right text-muted-foreground">{sub.tahun_komersil || '-'}</td>
                              <td className="px-3 py-2 text-right font-medium">{sub.mata_uang || 'IDR'} {formatCompact(sub.jumlah_aset)}</td>
                              <td className="px-3 py-2 text-right font-medium">{formatPercent(sub.persentase)}</td>
                            </tr>
                          ))}
                        </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-6 text-center text-xs text-muted-foreground">No subsidiaries data.</div>
                    )}
                  </div>
                </Card>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
